<template>
  <div
    ref="wrapper"
    class="status-select"
    :class="{
      'status-select--open': isOpen,
      'status-select--disabled': disabled,
      'status-select--dropup': dropUp,
      'status-select--destra': allineaDestra,
    }"
  >
    <button
      type="button"
      class="status-select__trigger"
      :disabled="disabled"
      :style="!optionsHaveIcons ? { borderColor: currentColor } : {}"
      @click="toggle"
    >
      <!-- Icona contestuale fissa (placeholder o selezionato senza icona propria) -->
      <i
        v-if="placeholderIcon && !(currentOption && currentOption.icon)"
        :class="'fa-solid ' + placeholderIcon"
        class="status-placeholder-single"
      ></i>
      <!-- Placeholder con icone multiple: nessuna selezione -->
      <span
        v-else-if="!currentOption && optionsHaveIcons"
        class="status-placeholder-icons"
      >
        <template v-for="(opt, i) in options" :key="opt.value">
          <i :class="'fa-solid ' + opt.icon"></i>
          <span v-if="i < options.length - 1" class="status-placeholder-sep"
            >/</span
          >
        </template>
      </span>
      <!-- Icona opzione selezionata -->
      <i
        v-else-if="currentOption && currentOption.icon"
        :class="'fa-solid ' + currentOption.icon"
      ></i>
      <!-- Dot (comportamento originale) -->
      <span
        v-else
        class="status-dot"
        :style="{ background: currentColor }"
      ></span>

      <span class="status-select__label">{{
        optionsHaveIcons && !currentOption ? "" : currentLabel
      }}</span>
      <i
        class="fa-solid"
        :class="isOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
      ></i>
    </button>
    <div
      v-if="isOpen"
      ref="dropdownEl"
      class="status-select__dropdown"
      :style="altezzaMax ? { maxHeight: `${altezzaMax}px` } : null"
    >
      <!-- [sc-48920] ricerca nella tendina (prop searchable) -->
      <div v-if="searchable" class="status-select__search">
        <i class="fa-solid fa-magnifying-glass"></i>
        <input
          ref="cercaEl"
          v-model="cerca"
          type="text"
          :placeholder="searchPlaceholder"
          :aria-label="searchPlaceholder"
          autocomplete="off"
          @keydown.enter.prevent="scegliPrima"
          @keydown.esc.prevent="chiudi"
        />
      </div>
      <button
        v-for="opt in opzioniVisibili"
        :key="String(opt.value)"
        type="button"
        class="status-select__option"
        :class="{ selected: isSelected(opt) }"
        :style="
          isSelected(opt) && opt.color ? { background: colorBg(opt.color) } : {}
        "
        :disabled="opt.disabled"
        @click="select(opt)"
      >
        <i v-if="opt.icon" :class="'fa-solid ' + opt.icon"></i>
        <span
          v-else
          class="status-dot"
          :style="{ background: opt.color }"
        ></span>
        <span>{{ opt.label }}</span>
        <i
          v-if="isSelected(opt)"
          class="fa-solid fa-check status-select__check"
          :style="opt.color ? { color: opt.color } : {}"
        ></i>
      </button>
      <div v-if="nessunRisultato" class="status-select__empty">
        Nessun risultato
      </div>
    </div>
  </div>
</template>

<script>
import { filtraOpzioni } from "@/utilities/filtriListino";

// Altezza massima della tendina: oltre, scorre al suo interno (v. .status-select__dropdown)
const DROPDOWN_MAX = 280;

export default {
  name: "StatusSelect",
  props: {
    modelValue: { default: null },
    options: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: "—" },
    placeholderIcon: { type: String, default: "" },
    /** [sc-48920] campo di ricerca in cima alla tendina */
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: "Cerca…" },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      isOpen: false,
      dropUp: false,
      allineaDestra: false,
      altezzaMax: null,
      cerca: "",
    };
  },
  computed: {
    currentOption() {
      return this.options.find((o) => o.value === this.modelValue);
    },
    currentLabel() {
      return this.currentOption?.label ?? this.placeholder;
    },
    currentColor() {
      return this.currentOption?.color ?? "#9ca3af";
    },
    optionsHaveIcons() {
      return this.options.some((o) => o.icon);
    },
    opzioniVisibili() {
      return this.searchable
        ? filtraOpzioni(this.options, this.cerca)
        : this.options;
    },
    // scritto qualcosa e resta solo «Tutti…» (o niente)
    nessunRisultato() {
      return (
        this.searchable &&
        this.cerca.trim() !== "" &&
        !this.opzioniVisibili.some((o) => o.value !== "")
      );
    },
  },
  watch: {
    // il testo cercato vale per una sola apertura
    isOpen(aperta) {
      if (!aperta) {
        this.cerca = "";
        this.allineaDestra = false;
        this.altezzaMax = null;
        return;
      }
      this.$nextTick(() => {
        this.adattaAltezza();
        // su schermi stretti la tendina uscirebbe a destra: si allinea al
        // bordo destro del pulsante
        const menu = this.$refs.dropdownEl?.getBoundingClientRect();
        if (menu && menu.right > window.innerWidth - 8)
          this.allineaDestra = true;
        if (this.searchable) this.$refs.cercaEl?.focus();
      });
    },
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onClickOutside);
  },
  methods: {
    // [sc-49299] La tendina non deve uscire dallo schermo: in una finestra
    // modale allungherebbe la pagina e farebbe scorrere tutto. Si misura la
    // sua altezza vera: verso l'alto se sotto non entra e sopra sì; se non
    // entra da nessuna parte, si accorcia e scorre al suo interno.
    adattaAltezza() {
      const menu = this.$refs.dropdownEl;
      const wrapper = this.$refs.wrapper;
      if (!menu || !wrapper) return;
      const MARGINE = 12;
      const rect = wrapper.getBoundingClientRect();
      const { alto, basso } = this.spazioVisibile();
      const sotto = basso - rect.bottom - MARGINE;
      const sopra = rect.top - alto - MARGINE;
      const serve = Math.min(menu.scrollHeight, DROPDOWN_MAX);
      if (serve <= sotto) {
        this.dropUp = false;
      } else if (serve <= sopra) {
        this.dropUp = true;
      } else {
        this.dropUp = sopra > sotto;
        this.altezzaMax = Math.max(Math.floor(Math.max(sopra, sotto)), 120);
      }
    },
    // Spazio in cui la tendina si vede: lo schermo, ristretto dal primo
    // contenitore che scorre o taglia (per esempio il corpo di una finestra
    // modale), che altrimenti la nasconderebbe o si metterebbe a scorrere
    spazioVisibile() {
      let alto = 0;
      let basso = window.innerHeight;
      for (
        let el = this.$refs.wrapper?.parentElement;
        el;
        el = el.parentElement
      ) {
        if (el === document.body || el === document.documentElement) break;
        const { overflowY } = window.getComputedStyle(el);
        if (["auto", "scroll", "hidden", "clip"].includes(overflowY)) {
          const r = el.getBoundingClientRect();
          alto = Math.max(alto, r.top);
          basso = Math.min(basso, r.bottom);
          break;
        }
      }
      return { alto, basso };
    },
    isSelected(opt) {
      return opt.value === this.modelValue;
    },
    toggle() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        // Si apre verso l'alto solo se sotto manca spazio per l'intera tendina
        // (fino a DROPDOWN_MAX px) e sopra ce n'e' di piu'
        const rect = this.$refs.wrapper.getBoundingClientRect();
        const sotto = window.innerHeight - rect.bottom;
        this.dropUp = sotto < DROPDOWN_MAX && rect.top > sotto;
        setTimeout(() => {
          document.addEventListener("click", this.onClickOutside);
        }, 0);
      } else {
        document.removeEventListener("click", this.onClickOutside);
      }
    },
    select(opt) {
      if (opt.disabled) return;
      this.$emit("update:modelValue", opt.value);
      this.isOpen = false;
      document.removeEventListener("click", this.onClickOutside);
    },
    // Invio: la prima voce trovata; con un testo scritto, esclusa «Tutti…»
    scegliPrima() {
      const voci = this.cerca.trim()
        ? this.opzioniVisibili.filter((o) => o.value !== "")
        : this.opzioniVisibili;
      const prima = voci.find((o) => !o.disabled);
      if (prima) this.select(prima);
    },
    chiudi() {
      this.isOpen = false;
      document.removeEventListener("click", this.onClickOutside);
    },
    colorBg(hex) {
      if (!hex) return "transparent";
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    },
    onClickOutside(e) {
      if (!this.$refs.wrapper?.contains(e.target)) {
        this.isOpen = false;
        document.removeEventListener("click", this.onClickOutside);
      }
    },
  },
};
</script>

<style scoped>
.status-select {
  position: relative;
  display: inline-block;
}

.status-select__trigger {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border-radius: 20px;
  border: 1.5px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  min-width: 108px;
  transition: border-color 0.15s;
}

.status-select__trigger:hover:not(:disabled) {
  border-color: #bbb;
}

.status-select--open .status-select__trigger {
  border-color: #999;
}

.status-select--disabled .status-select__trigger {
  opacity: 0.7;
  cursor: not-allowed;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-placeholder-single {
  font-size: 13px;
  color: #9ca3af;
}

.status-placeholder-icons {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #9ca3af;
}

.status-placeholder-icons i {
  font-size: 12px;
}

.status-placeholder-sep {
  font-size: 11px;
  color: #d1d5db;
  line-height: 1;
}

.status-select__label {
  flex: 1;
  text-align: left;
}

.status-select__trigger > i.fa-solid {
  font-size: 10px;
  color: #888;
}

.status-select__trigger > i:not(.fa-chevron-up):not(.fa-chevron-down) {
  font-size: 13px;
  color: #374151;
}

.status-select__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  bottom: auto;
  left: 0;
  min-width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 200;
  padding: 4px;
  /* Liste lunghe (es. Tipo sanitari, 12 voci): scroll interno invece di
     allungare la pagina. Tenere allineato a DROPDOWN_MAX nello script */
  max-height: 280px;
  overflow-y: auto;
}

/* [sc-48920] campo di ricerca fisso in cima alla tendina, con lo stesso
   aspetto della ricerca degli elenchi (angoli 8px, lente a sinistra) */
.status-select__search {
  position: sticky;
  top: -4px;
  z-index: 1;
  margin: -4px -4px 4px;
  padding: 8px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.status-select__search i {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  color: #9ca3af;
  pointer-events: none;
}

.status-select__search input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  height: 32px;
  padding: 0 10px 0 28px;
  border-radius: 8px;
  font-size: 12px;
}

/* schermi stretti: la tendina parte dal bordo destro del pulsante e non
   supera la larghezza dello schermo */
.status-select--destra .status-select__dropdown {
  left: auto;
  right: 0;
}

.status-select__dropdown {
  max-width: calc(100vw - 32px);
}

.status-select__empty {
  padding: 8px 10px;
  font-size: 12px;
  color: #9ca3af;
}

.status-select__option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  text-align: left;
  transition: background 0.1s;
}

.status-select__option:hover:not(:disabled) {
  background: #f5f5f5;
}

.status-select__option:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.status-select__option i:not(.status-select__check) {
  font-size: 13px;
  color: #374151;
}

.status-select__check {
  margin-left: auto;
  font-size: 11px;
}

.status-select--dropup .status-select__dropdown {
  top: auto;
  bottom: calc(100% + 4px);
}
</style>
